import { useMemo, type ReactNode } from 'react';
import { ApolloProvider } from '@apollo/client/react';
import { Provider as ReduxProvider } from 'react-redux';
import { Alert, CssBaseline, Snackbar, ThemeProvider } from '@mui/material';

import { apolloClient } from './apollo';
import { useAppDispatch, useAppSelector } from './hooks';
import { store } from './store';
import { dismissToast } from './uiSlice';
import { ServiceWorkerPrompt } from './ServiceWorkerPrompt';
import { AuthProvider } from '@/features/auth/AuthProvider';
import { AppErrorBoundary } from '@/features/misc/ErrorPage';
import { createAppTheme } from '@/theme/theme';

export function Providers({ children }: { children: ReactNode }) {
  return (
    <ReduxProvider store={store}>
      <ThemedApp>
        <AppErrorBoundary>
          <ApolloProvider client={apolloClient}>
            <AuthProvider>{children}</AuthProvider>
          </ApolloProvider>
        </AppErrorBoundary>
      </ThemedApp>
    </ReduxProvider>
  );
}

function ThemedApp({ children }: { children: ReactNode }) {
  const theme = useMemo(() => createAppTheme(), []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
      <Toasts />
      <ServiceWorkerPrompt />
    </ThemeProvider>
  );
}

function Toasts() {
  const dispatch = useAppDispatch();
  const toasts = useAppSelector((state) => state.ui.toasts);
  const current = toasts[0];

  return (
    <Snackbar
      key={current?.id}
      open={Boolean(current)}
      autoHideDuration={current?.severity === 'error' ? 8000 : 4000}
      onClose={(_, reason) => {
        if (reason === 'clickaway') return;
        if (current) dispatch(dismissToast(current.id));
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity={current?.severity ?? 'success'}
        variant="filled"
        onClose={() => current && dispatch(dismissToast(current.id))}
        sx={{ minWidth: 280 }}
      >
        {current?.message}
      </Alert>
    </Snackbar>
  );
}
